/* ==========================================================================
   Vidalite — Product Detail Page
   - Variant selection (finish swatch + color temp pill) drives variant id,
     hero image, and price.
   - Gallery prev/next + counter
   - Accordion expand/collapse
   - Applications carousel arrow nav + counter
   - Quote modal open/close/submit
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {

    /* Mobile menu toggle is handled globally in v3-main.js (event-delegated).
       The duplicate handler that used to live here double-toggled the drawer
       on product pages, so it opened then immediately closed. Removed. */

    /* =====================================================================
       VARIANT SELECTION
       Finish swatches and color-temp pills both write into a shared
       options[] array keyed by data-option-index. A change re-resolves
       the matching variant, swaps the hero image, updates the price,
       and writes the variant id into the cart-form hidden input.
       ===================================================================== */

    const productEl = document.querySelector('.product');
    const variantJsonEl = document.querySelector('[data-variant-json]');
    let variants = [];
    if (variantJsonEl) {
        try { variants = JSON.parse(variantJsonEl.textContent); }
        catch (_) { variants = []; }
    }

    // Product media list — used to build the per-finish gallery.
    const mediaJsonEl = document.querySelector('[data-product-media]');
    let productMedia = [];
    if (mediaJsonEl) {
        try { productMedia = JSON.parse(mediaJsonEl.textContent); }
        catch (_) { productMedia = []; }
    }

    /* ── Finish ↔ image association ───────────────────────────────────────
       Each media's alt carries its finish ("… — Brushed Brass"); lifestyle
       shots are tagged generically ("… — lifestyle") BUT are uploaded right
       after their finish's detail shots, so they inherit the current finish
       block. Dimensions / infographic stay shared across every finish.
       Result: selecting a finish shows only that finish's photos. */
    let finishOptionIndex = -1;   // -1 ⇒ product has no finish axis → no per-finish filtering
    const firstSwatchEl = document.querySelector('.product__finish-item[data-option-index]');
    if (firstSwatchEl) finishOptionIndex = parseInt(firstSwatchEl.dataset.optionIndex || '0', 10);
    const finishValues = finishOptionIndex < 0 ? [] : [...new Set(
        variants.map(v => (v.options || [])[finishOptionIndex]).filter(Boolean)
    )];
    function finishFromAlt(alt) {
        const a = (alt || '').toLowerCase();
        let best = null;
        // Longest containing match wins so "Brass And Black" beats "Black",
        // and "White 3000K" alt still resolves to the "White" finish.
        finishValues.forEach(fv => {
            if (a.indexOf(fv.toLowerCase()) !== -1 && (!best || fv.length > best.length)) best = fv;
        });
        return best;
    }
    /* ── Style ↔ image association (consolidated products, e.g. Libra) ─────
       Some products carry a "Style" axis (Trim / Trimless / Gimbal) where the
       graphics — exploded views, dimension diagrams, lifestyle — differ per
       style, not per finish. Detect that axis by its distinctive values and,
       when present: (a) tag each media with its style from the alt, and (b)
       treat non-hero graphics as shared across finish but style-specific, so
       selecting Trim shows only Trim imagery. Products without a Style axis
       (Shura, etc.) keep styleOptionIndex === -1 and are unaffected. */
    const STYLE_TOKENS = ['Trim', 'Trimless', 'Gimbal'];
    const optCount = (variants[0] && variants[0].options) ? variants[0].options.length : 0;
    let styleOptionIndex = -1;
    for (let i = 0; i < optCount; i++) {
        const vals = [...new Set(variants.map(v => (v.options || [])[i]).filter(Boolean))];
        if (vals.length && vals.every(x => STYLE_TOKENS.indexOf(x) !== -1)) { styleOptionIndex = i; break; }
    }
    const styleValues = styleOptionIndex < 0 ? [] : [...new Set(
        variants.map(v => (v.options || [])[styleOptionIndex]).filter(Boolean)
    )];
    function styleFromAlt(alt) {
        const a = (alt || '').toLowerCase();
        let best = null;
        // Longest match so "Trimless" wins over its "Trim" substring.
        styleValues.forEach(sv => {
            if (a.indexOf(sv.toLowerCase()) !== -1 && (!best || sv.length > best.length)) best = sv;
        });
        return best;
    }
    // Hero images = the variants' featured images; everything else is a graphic.
    const heroSrcSet = new Set(
        variants.map(v => (v.featured_image && (v.featured_image.src || v.featured_image)) || '').filter(Boolean)
    );

    (function tagMediaFinishes() {
        let lastFinish = null;
        productMedia.forEach(m => {
            let f = finishFromAlt(m.alt);
            if (m.kind === 'dimensions' || m.kind === 'infographic') {
                f = null;                       // genuinely shared across finishes
            } else if (styleOptionIndex >= 0 && !heroSrcSet.has(m.src)) {
                f = null;                       // consolidated product: only the finish
                                                // heroes are finish-specific; graphics are
                                                // shared across finish (filtered by style)
            } else if (!f) {
                // A variant OR lifestyle shot whose alt didn't name a finish
                // inherits the current finish block (by upload order) rather than
                // falling into the shared bucket — otherwise it would leak into
                // every finish's gallery.
                f = lastFinish;
            }
            if (f) lastFinish = f;
            m.finish = f;                       // null ⇒ shared (shown for every finish)
        });
    })();

    (function tagMediaStyles() {
        if (styleOptionIndex < 0) return;
        let lastStyle = null;
        productMedia.forEach(m => {
            let s = styleFromAlt(m.alt);
            if (!s) s = lastStyle;              // inherit current style block by upload order
            if (s) lastStyle = s;
            m.style = s;                        // null ⇒ shared across styles
        });
    })();

    const mediaFinishByUrl = new Map(productMedia.map(m => [m.src, m.finish]));

    const variantIdInput = document.querySelector('[data-variant-id-input]');
    const priceEl        = document.querySelector('[data-product-price]');
    const cartLabelEl    = document.querySelector('[data-cart-btn-label]');
    const cartBtn        = document.querySelector('.product__cart-btn');

    /* --- Sticky Add-to-Cart bar --------------------------------------------
       Proxies the real buy button and shows whenever that button is off
       screen (so it's there immediately on mobile, and on scroll on desktop).*/
    const stickyBar     = document.querySelector('[data-sticky-atc]');
    const stickyPriceEl = document.querySelector('[data-sticky-price]');
    const stickyLabelEl = document.querySelector('[data-sticky-btn-label]');
    const stickyAddBtn  = document.querySelector('[data-sticky-add]');
    const actionsEl     = document.querySelector('.product__actions');

    if (stickyAddBtn && cartBtn) {
        stickyAddBtn.addEventListener('click', () => { if (!cartBtn.disabled) cartBtn.click(); });
    }

    /* ── Description "Read more / less" ───────────────────────────────
       .product__desc is line-clamped to 4 lines. Reveal the toggle only
       when the copy actually overflows, then expand / collapse on click. */
    const descEl     = document.querySelector('[data-desc]');
    const descToggle = document.querySelector('[data-desc-toggle]');
    if (descEl && descToggle) {
        const descOverflows = () => descEl.scrollHeight - descEl.clientHeight > 2;
        const syncDescToggle = () => {
            if (descEl.classList.contains('is-expanded')) return; // keep visible while open
            descToggle.hidden = !descOverflows();
        };
        syncDescToggle();
        window.addEventListener('resize', syncDescToggle);
        descToggle.addEventListener('click', () => {
            const expanded = descEl.classList.toggle('is-expanded');
            descToggle.textContent = expanded ? 'Read less' : 'Read more';
        });
    }
    /* Desktop: reveal the sticky bar only AFTER the inline Add-to-Cart has
       scrolled fully above the viewport — not while it's still below the fold
       on landing. Mobile keeps the always-available bar whenever the inline
       button is off-screen (unchanged). 901px = the hero's single-column
       breakpoint. */
    if (stickyBar && actionsEl) {
        const mqDesktop = window.matchMedia('(min-width: 901px)');
        const setSticky = show => {
            stickyBar.classList.toggle('is-visible', show);
            stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
        };
        const REVEAL_OFFSET = 120;   // px — reveal slightly BEFORE the inline ATC fully leaves the top
        const evalSticky = () => {
            const r = actionsEl.getBoundingClientRect();
            const scrolledPast = r.bottom <= REVEAL_OFFSET;                  // inline ATC nearly past the top
            const offScreen    = r.bottom <= 0 || r.top >= window.innerHeight; // above OR below
            setSticky(mqDesktop.matches ? scrolledPast : offScreen);
        };
        let ticking = false;
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => { evalSticky(); ticking = false; });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        if (mqDesktop.addEventListener) mqDesktop.addEventListener('change', evalSticky);
        else if (mqDesktop.addListener) mqDesktop.addListener(evalSticky);
        evalSticky();
    }
    const mainImage      = document.getElementById('mainImage');
    const galleryEl      = document.querySelector('.product__gallery');
    const counterCurrentTop = document.querySelector('.product__gallery-counter-current');
    const counterTotalTop   = document.querySelector('.product__gallery-counter-total');

    // Per-variant gallery list. Rebuilt every time the selection changes.
    let galleryList = [];
    let galleryIdx = 0;

    function variantImageSrc(variant) {
        if (!variant) return '';
        const fi = variant.featured_image;
        if (!fi) return '';
        return fi.src || fi || '';
    }

    function buildGalleryList(variant) {
        const out = [];
        const seen = new Set();
        const push = url => {
            if (!url) return;
            if (seen.has(url)) return;
            seen.add(url);
            out.push(url);
        };
        const finishName = (variant && finishOptionIndex >= 0) ? (variant.options || [])[finishOptionIndex] : null;
        const styleName  = (variant && styleOptionIndex  >= 0) ? (variant.options || [])[styleOptionIndex]  : null;
        // On a Style-axis product, only keep media of the selected style
        // (media with no style are treated as shared). No-op elsewhere.
        const styleOK = m => !styleName || !m.style || m.style === styleName;
        // 1) this finish's primary shot first — but never LEAD with an image that
        //    is tagged to a different finish (admin mis-assignment / OOS fallback).
        const featured = variantImageSrc(variant);
        const featuredFinish = mediaFinishByUrl.has(featured) ? mediaFinishByUrl.get(featured) : finishName;
        if (!finishName || !featuredFinish || featuredFinish === finishName) push(featured);
        // 2) every image for the selected finish (detail + lifestyle), in order
        if (finishName) productMedia.forEach(m => { if (m.finish === finishName && styleOK(m)) push(m.src); });
        // 3) genuinely-shared media (dimensions / infographic / pre-block) last,
        //    restricted to the selected style when the product has a Style axis.
        productMedia.forEach(m => { if (!m.finish && styleOK(m)) push(m.src); });
        return out;
    }

    // Preload cache — every variant image + shared media is fetched once
    // up front so swapping finishes is instant (no network wait, no blank
    // flash). Keeps the decoded bitmaps warm in the browser cache.
    const preloadCache = new Set();
    function preload(url) {
        if (!url || preloadCache.has(url)) return;
        preloadCache.add(url);
        const im = new Image();
        im.src = url;
    }
    function preloadAll() {
        // Warm every finish's primary shot so switching finishes is instant.
        variants.forEach(v => preload(variantImageSrc(v)));
        // Plus genuinely-shared media.
        productMedia.forEach(m => { if (!m.finish) preload(m.src); });
    }

    function renderGalleryAt(i) {
        if (!galleryList.length || !mainImage) return;
        galleryIdx = (i + galleryList.length) % galleryList.length;
        const url = galleryList[galleryIdx];
        if (mainImage.getAttribute('src') !== url) {
            const pre = new Image();
            pre.src = url;
            const swap = () => { mainImage.src = url; mainImage.style.opacity = '1'; };
            if (pre.complete) {
                // already cached (preloaded) → swap instantly, no flicker
                swap();
            } else {
                // not yet loaded → dim slightly, swap the moment it's ready
                mainImage.style.opacity = '0.4';
                pre.onload = swap;
                pre.onerror = swap;
            }
        }
        if (counterCurrentTop) counterCurrentTop.textContent = String(galleryIdx + 1).padStart(2, '0') + '/';
        if (counterTotalTop)   counterTotalTop.textContent   = String(galleryList.length).padStart(2, '0');
        if (galleryEl)         galleryEl.dataset.images       = galleryList.join('|');
    }

    function rebuildGallery(variant) {
        galleryList = buildGalleryList(variant);
        galleryList.forEach(preload);   // warm this finish's full set
        renderGalleryAt(0);
    }

    // Seed options[] from the currently selected variant (server-rendered).
    let selectedOptions = [];
    const initialVariantId = parseInt(productEl?.dataset?.currentVariantId || '0', 10);
    const initialVariant = variants.find(v => v.id === initialVariantId) || variants[0];
    if (initialVariant) selectedOptions = (initialVariant.options || []).slice();

    // Price comes from Liquid as a decimal string ("98.44") — display verbatim.
    function formatMoney(p) {
        const n = typeof p === 'number' ? p : parseFloat(p);
        if (isNaN(n)) return '';
        return '$' + n.toFixed(n % 1 === 0 ? 0 : 2);
    }

    function findMatchingVariant() {
        return variants.find(v => {
            const opts = v.options || [];
            if (opts.length !== selectedOptions.length) return false;
            return selectedOptions.every((val, i) => opts[i] === val);
        });
    }

    // Walk every option pill / swatch and disable it if the value, combined
    // with the currently-selected values on OTHER axes, has no available
    // variant. Catches things like "24" + 6000lm doesn't exist" — the
    // 6000lm pill greys out when 24" is selected.
    function refreshAvailability() {
        document.querySelectorAll('[data-option-index][data-option-value]').forEach(btn => {
            const idx = parseInt(btn.dataset.optionIndex, 10);
            const val = btn.dataset.optionValue;
            const available = variants.some(v => {
                const opts = v.options || [];
                if (opts[idx] !== val) return false;
                for (let j = 0; j < selectedOptions.length; j++) {
                    if (j === idx) continue;
                    if (selectedOptions[j] !== opts[j]) return false;
                }
                return v.available !== false;
            });
            btn.classList.toggle('is-disabled', !available);
            if (available) btn.removeAttribute('disabled');
            else           btn.setAttribute('disabled', 'disabled');
        });
    }

    // Product-level fallback specs — used when a variant's metafield is empty.
    const productSpecsEl = document.querySelector('[data-product-specs]');
    let productSpecs = {};
    if (productSpecsEl) {
        try { productSpecs = JSON.parse(productSpecsEl.textContent); }
        catch (_) { productSpecs = {}; }
    }

    function refreshSpecTiles(variant) {
        // Variant spec wins; product spec is the fallback. Empty after that
        // → hide the tile.
        document.querySelectorAll('.features__tile[data-tile-key]').forEach(tile => {
            const key = tile.dataset.tileKey;
            const variantValue = variant && variant.specs && variant.specs[key];
            const value = (variantValue && String(variantValue).trim())
                       || (productSpecs[key] && String(productSpecs[key]).trim())
                       || '';
            const valueEl = tile.querySelector('[data-tile-value]');
            if (valueEl) valueEl.textContent = value;
            tile.style.display = value ? '' : 'none';
        });
    }

    function applySelectedVariant() {
        const variant = findMatchingVariant();

        // The gallery always reflects the selected FINISH, even when the full
        // option combo (e.g. size + mount + finish) has no in-stock variant —
        // otherwise the gallery would keep showing the previous finish.
        const selFinish = selectedOptions[finishOptionIndex];
        const galleryVariant = variant
            || variants.find(v => (v.options || [])[finishOptionIndex] === selFinish)
            || initialVariant;
        rebuildGallery(galleryVariant);

        if (!variant) {
            // No variant matches the current combo (e.g. user picked an unavailable
            // mix) — keep button states accurate so they can recover.
            if (cartLabelEl) cartLabelEl.textContent = 'Out of Stock';
            if (cartBtn)     cartBtn.disabled        = true;
            if (stickyLabelEl) stickyLabelEl.textContent = 'Out of Stock';
            if (stickyAddBtn)  stickyAddBtn.disabled     = true;
            refreshAvailability();
            return;
        }

        if (variantIdInput) variantIdInput.value = variant.id;
        if (productEl)      productEl.dataset.currentVariantId = String(variant.id);

        // Price + availability
        if (priceEl)     priceEl.textContent     = formatMoney(variant.price);
        if (cartLabelEl) cartLabelEl.textContent = variant.available ? 'Add to Cart' : 'Out of Stock';
        if (cartBtn)     cartBtn.disabled        = !variant.available;
        // Mirror into the sticky bar.
        if (stickyPriceEl) stickyPriceEl.textContent = formatMoney(variant.price);
        if (stickyLabelEl) stickyLabelEl.textContent = variant.available ? 'Add to Cart' : 'Out of Stock';
        if (stickyAddBtn)  stickyAddBtn.disabled      = !variant.available;

        // (Gallery already rebuilt above so it tracks the selected finish.)

        // Spec tiles — swap to this variant's specs (with product-level fallback).
        refreshSpecTiles(variant);

        refreshAvailability();

        // Sync URL ?variant=… so refreshes preserve the selection
        try {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', String(variant.id));
            window.history.replaceState({}, '', url.toString());
        } catch (_) { /* ignore */ }
    }

    // Warm the cache with every variant image so finish swaps are instant.
    preloadAll();

    // Seed the gallery once on load so prev/next + counter are aligned
    // with the initial variant before the user touches anything.
    if (initialVariant) rebuildGallery(initialVariant);
    // Seed pill availability so unreachable combos are greyed out on first paint.
    refreshAvailability();

    // Finish swatches
    document.querySelectorAll('.product__finish-item').forEach(btn => {
        btn.addEventListener('click', () => {
            const idx = parseInt(btn.dataset.optionIndex || '0', 10);
            const value = btn.dataset.optionValue;
            selectedOptions[idx] = value;

            // Active swatch state — only one swatch per finish option.
            document.querySelectorAll(`.product__finish-item[data-option-index="${idx}"]`).forEach(b => {
                b.classList.toggle('product__finish-item--active', b === btn);
            });

            applySelectedVariant();
        });
    });

    // Color-temp / generic option pills
    document.querySelectorAll('.product__option-pill').forEach(btn => {
        btn.addEventListener('click', () => {
            const idx = parseInt(btn.dataset.optionIndex || '0', 10);
            const value = btn.dataset.optionValue;
            selectedOptions[idx] = value;

            document.querySelectorAll(`.product__option-pill[data-option-index="${idx}"]`).forEach(b => {
                b.classList.toggle('product__option-pill--active', b === btn);
            });

            applySelectedVariant();
        });
    });


    /* =====================================================================
       GALLERY — PREV / NEXT navigation
       Drives the per-variant galleryList that rebuildGallery() populates.
       ===================================================================== */

    const prevBtn = document.querySelector('.product__gallery-btn--prev');
    const nextBtn = document.querySelector('.product__gallery-btn--next');
    if (prevBtn) prevBtn.addEventListener('click', () => renderGalleryAt(galleryIdx - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => renderGalleryAt(galleryIdx + 1));


    /* --- Accordions: Expand / Collapse ----------------------------------- */
    document.querySelectorAll('.product__accordion').forEach(acc => {
        const header = acc.querySelector('.product__accordion-header');
        const icon = acc.querySelector('.product__accordion-icon');
        if (!header) return;
        header.addEventListener('click', () => {
            const isOpen = acc.classList.toggle('is-open');
            if (icon) icon.textContent = isOpen ? '−' : '+';
        });
    });


    /* =====================================================================
       APPLICATIONS — Horizontal scroll carousel with arrow nav + counter
       ===================================================================== */

    const appsSection = document.querySelector('.applications');
    const appsTrack   = document.querySelector('.applications__track');
    const appsCards   = document.querySelectorAll('.applications__card');
    const appsPrev    = document.querySelector('.applications [data-prev]');
    const appsNext    = document.querySelector('.applications [data-next]');
    const appsCurrent = document.querySelector('.applications [data-current]');
    const appsTotal   = document.querySelector('.applications [data-total]');
    const appsFilters = document.querySelectorAll('.applications [data-app-filter-btn]');

    if (appsTrack && appsCards.length) {
        // Visible cards = those not hidden by the filter (we read computed
        // display so the same logic works regardless of how filtering is
        // implemented in CSS).
        function visibleCards() {
            return Array.from(appsCards).filter(c => c.offsetParent !== null);
        }

        function updateAppsTotal() {
            const n = visibleCards().length;
            if (appsTotal) appsTotal.textContent = String(n).padStart(2, '0');
        }

        function updateAppsCounter() {
            const trackLeft = appsTrack.getBoundingClientRect().left;
            const cards = visibleCards();
            if (cards.length === 0) {
                if (appsCurrent) appsCurrent.textContent = '00';
                return;
            }
            let closestIdx = 0;
            let closestDist = Infinity;
            cards.forEach((card, i) => {
                const dist = Math.abs(card.getBoundingClientRect().left - trackLeft);
                if (dist < closestDist) {
                    closestDist = dist;
                    closestIdx = i;
                }
            });
            if (appsCurrent) appsCurrent.textContent = String(closestIdx + 1).padStart(2, '0');
        }

        updateAppsTotal();
        appsTrack.addEventListener('scroll', updateAppsCounter, { passive: true });
        updateAppsCounter();

        function appsScrollStep() {
            const cards = visibleCards();
            if (cards.length === 0) return 0;
            const styles = getComputedStyle(appsTrack);
            const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
            return cards[0].getBoundingClientRect().width + gap;
        }

        if (appsPrev) appsPrev.addEventListener('click', () => {
            appsTrack.scrollBy({ left: -appsScrollStep(), behavior: 'smooth' });
        });
        if (appsNext) appsNext.addEventListener('click', () => {
            appsTrack.scrollBy({ left: appsScrollStep(), behavior: 'smooth' });
        });

        // Filter toggle — set data-app-filter on the section so CSS hides
        // non-matching cards, then reset the carousel to the new first.
        appsFilters.forEach(btn => {
            btn.addEventListener('click', () => {
                const value = btn.getAttribute('data-app-filter-btn');
                if (appsSection) appsSection.setAttribute('data-app-filter', value);
                appsFilters.forEach(b => {
                    const active = b === btn;
                    b.classList.toggle('applications__filter-btn--active', active);
                    b.setAttribute('aria-selected', active ? 'true' : 'false');
                });
                appsTrack.scrollTo({ left: 0, behavior: 'auto' });
                updateAppsTotal();
                updateAppsCounter();
            });
        });
    }


    /* =====================================================================
       QUOTE MODAL — open / close / submit
       Lightweight inline confirmation when no quote_action_url is set.
       ===================================================================== */

    const modal    = document.getElementById('quoteModal');
    const form     = document.getElementById('quoteForm');
    const success  = document.getElementById('quoteSuccess');
    const closeBtn = modal?.querySelector('.quote-modal__close');
    const backdrop = modal?.querySelector('.quote-modal__backdrop');
    const dialogEl = modal?.querySelector('.quote-modal__dialog');

    let releaseTrap = null;

    function openModal(e) {
        if (e) e.preventDefault();
        if (!modal) return;
        const trigger = e && e.currentTarget;
        modal.removeAttribute('hidden');
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        if (form) form.style.display = '';
        if (success) success.style.display = 'none';
        if (window.VidaliteA11y && dialogEl) {
            releaseTrap = window.VidaliteA11y.trapFocus(dialogEl, {
                onEscape: closeModal,
                restoreEl: trigger,
            });
        }
    }
    function closeModal() {
        if (!modal) return;
        modal.classList.remove('is-open');
        modal.setAttribute('hidden', '');
        document.body.style.overflow = '';
        if (releaseTrap) { releaseTrap(); releaseTrap = null; }
    }

    document.querySelectorAll('[data-quote-trigger], a[href="#quote"]').forEach(t => {
        t.addEventListener('click', openModal);
    });
    closeBtn?.addEventListener('click', closeModal);
    backdrop?.addEventListener('click', closeModal);

    form?.addEventListener('submit', e => {
        // If the merchant configured a quote_action_url, let the browser submit
        // normally so the form posts. Otherwise show the inline success state.
        const hasAction = form.action && form.action !== '#' && !form.action.endsWith('#');
        if (!hasAction) {
            e.preventDefault();
            if (form) form.style.display = 'none';
            if (success) success.style.display = 'block';
        }
    });
});


/* =========================================================================
   Hardwired connection → sister-product redirect.

   Plug-in products carry a Connection: Plug-in / Hardwired option but the
   hardwired SKU is actually a separate product with its own (broader)
   finish palette. So clicking Hardwired on the plug-in PDP should not
   try to select a half-valid variant on this product — it should send
   the customer to the dedicated hardwired SKU, with the currently-selected
   finish preserved in the URL where possible.

   The sister handle lives on the form: data-sister-handle="<handle>".
   It's written from the vidalite.sister_handle product metafield; if
   the metafield is empty we don't intercept (lets the product behave as
   a normal multi-variant product).
   ========================================================================= */
document.addEventListener('DOMContentLoaded', () => {
    const form = document.querySelector('[data-product-form][data-sister-handle]');
    if (!form) return;
    const sister = form.dataset.sisterHandle;
    if (!sister) return;

    document.addEventListener('click', e => {
        const btn = e.target.closest('.product__connection-item');
        if (!btn) return;
        const value = (btn.dataset.optionValue || '').toLowerCase();
        if (!value.includes('hardwire')) return;
        // Bail out — don't run Shopify's variant-selection handler.
        e.preventDefault();
        e.stopPropagation();

        // Preserve the currently-selected finish so the sister PDP loads
        // with the same colour pre-picked when possible. Finish swatch
        // active state lives on .product__finish-item--active.
        let finish = '';
        const activeFinish = document.querySelector('.product__finish-item--active:not(.product__connection-item)');
        if (activeFinish && activeFinish.dataset.optionValue) {
            finish = activeFinish.dataset.optionValue;
        }
        const url = '/products/' + sister + (finish ? '?finish=' + encodeURIComponent(finish) : '');
        window.location.assign(url);
    }, true);  // capture so we beat the swatch-handler that would otherwise re-select the variant
});

/* ── Bazaarvoice reviews reveal ────────────────────────────────────────
   The reviews section renders collapsed (.is-pending) so it has zero
   footprint until Bazaarvoice fills it. We keep the container in normal
   flow (never display:none) so BV's scroll-lazy-load still fires, and drop
   .is-pending the instant BV adds any content. If BV never renders (e.g.
   the product is not in the BV catalog), the section simply stays
   invisible — no empty heading. */
(function () {
  function init() {
    var sec = document.querySelector('.prod-reviews[data-bv-reviews]');
    if (!sec) return;
    // "Filled" = Bazaarvoice has rendered into the container. BV often uses a
    // cross-origin iframe, so its text/child nodes are invisible to us — but
    // the rendered widget gives the container real height. When the section is
    // collapsed-and-empty the container is ~0px, so height is the reliable
    // signal across all of BV's render paths (iframe, divs, write-review CTA).
    function filled() {
      var c = sec.querySelector('[data-bv-show="reviews"]');
      return !!c && c.getBoundingClientRect().height > 24;
    }
    function reveal() { if (filled()) { sec.classList.remove('is-pending'); return true; } return false; }
    if (reveal()) return;
    var mo = new MutationObserver(reveal);
    mo.observe(sec, { childList: true, subtree: true, characterData: true });
    // Safety net for any render path the observer misses, then stop.
    var n = 0, iv = setInterval(function () { if (reveal() || ++n > 30) clearInterval(iv); }, 400);
    setTimeout(function () { try { mo.disconnect(); } catch (e) {} }, 15000);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
